import React, { FC, ReactNode, useState } from 'react';
import { useTranslate } from '@/translations';
import { Alert, Input, Modal, Spin } from 'antd';
import { faClose, faSave, Icon } from '@/ui/components';

export type AuthorizationNotesModalProps = {
  visible: boolean;
  title: ReactNode;
  loading: boolean;
  onClose: () => void;
  onConfirm: (comment?: string) => Promise<void>;
  // allows different "comment required" rules per caller
  isConfirmDisabled: (comment?: string) => boolean;
};

export const AuthorizationNotesModal: FC<AuthorizationNotesModalProps> = ({
  visible,
  title,
  loading,
  onClose,
  onConfirm,
  isConfirmDisabled,
}) => {
  const { t } = useTranslate();
  const [comment, setComment] = useState<string>();
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const closeModel = () => {
    if (submitting) return;
    setComment(undefined);
    setError(undefined);
    onClose();
  };

  const isBusy = loading || submitting;
  const isConfirmBtnDisabled = isBusy || isConfirmDisabled(comment);

  return (
    <Modal
      open={visible}
      onCancel={closeModel}
      title={title}
      centered
      closable={false}
      okText={t('yes')}
      cancelText={t('cancel')}
      okButtonProps={{
        disabled: isConfirmBtnDisabled,
        icon: <Icon icon={faSave} />,
      }}
      cancelButtonProps={{
        disabled: isBusy,
        icon: <Icon icon={faClose} />,
      }}
      confirmLoading={isBusy}
      onOk={async () => {
        if (isConfirmBtnDisabled) return;

        setError(undefined);
        setSubmitting(true);
        try {
          await onConfirm(comment);
          closeModel();
        } catch (e) {
          setError(
            (e as any)?.response?.data?.message ||
              (e as any)?.message ||
              (e as Error).message ||
              'Something went wrong...',
          );
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <Spin spinning={isBusy}>
        <Input.TextArea
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('addNote')}
          required
        />

        {error ? <Alert message={error} type="error" className="mb-4" /> : null}
      </Spin>
    </Modal>
  );
};
